import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio, { RadioPresentationProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonPresentationProps } from "@waytek/MobiusOverrides/Button/Button";
import Modal, { ModalPresentationProps } from "@waytek/MobiusOverrides/Modal/Modal";
import { ToastBody, ToastStyle } from "@waytek/MobiusOverrides/Toast/Toast";
import { CustomState } from "@waytek/Store/Reducers";
import { AvalaraAddressModel, AvalaraValidationResponse } from "@waytek/Types/AvalaraValidationModel";
import { colorPalette } from "@waytek/Utilities/variables";
import { HorizontalDividerWidget } from "@waytek/Widgets/Basic/HorizontalDivider";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    key: string;
    showModal: boolean;
    handleModalClose: () => void;
    handleModalSubmit: (btAddress?: AvalaraAddressModel, stAddress?: AvalaraAddressModel) => void;
    enteredBtAddress: AvalaraAddressModel;
    enteredStAddress: AvalaraAddressModel;
    addresses: AvalaraValidationResponse;
    extendedStyles?: AvalaraValidationComponentModalStyles;
    useBillingAddress: boolean;
}

const mapStateToProps = (state: CustomState) => ({});

export type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface AvalaraValidationComponentModalStyles {
    modalWrapper?: ModalPresentationProps;
    formWrapper?: InjectableCss;
    mainContainer?: GridContainerProps;
    mainGridItem?: GridItemProps;
    columGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    label?: TypographyPresentationProps;
    extraLabel?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    addressInfo?: TypographyPresentationProps;
    radioGroup?: RadioGroupPresentationProps;
    radioItem?: RadioPresentationProps;
    submitButton?: ButtonPresentationProps;
    enteredButton?: ButtonPresentationProps;
}

const avalaraValidationComponentModalStyles: AvalaraValidationComponentModalStyles = {
    mainContainer: { gap: 20 },
    mainGridItem: { width: 12 },
    columGridItem: { width: [12, 12, 6, 6, 6] },
    buttonsGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    label: { variant: "body", weight: 700 },
    description: { variant: "body" },
    addressInfo: {
        mergeCss: true,
        css: css`
            display: block;
        `,
    },
    radioGroup: {
        mergeCss: true,
        css: css`
            & > ${TypographyStyle} {
                font-size: 16px;
                line-height: 24px;
                font-weight: 700;
            }
        `,
    },
    submitButton: {
        variant: "primary",
        css: css`
            margin-left: 20px;
        `,
    },
    enteredButton: {
        variant: "secondary",
    },
};

const AvalaraValidationComponentModal = ({
    key,
    showModal,
    handleModalClose,
    handleModalSubmit,
    enteredBtAddress,
    enteredStAddress,
    addresses,
    extendedStyles,
    useBillingAddress,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(avalaraValidationComponentModalStyles, extendedStyles));
    const [addressBtSelectedIdx, setAddressBtSelectedIdx] = useState("0");
    const [addressStSelectedIdx, setAddressStSelectedIdx] = useState("0");

    const buildAddress = (address: AvalaraAddressModel) => (
        <Typography>
            <Typography {...styles.addressInfo}>{address.line1}</Typography>
            {address.line2 && <Typography {...styles.addressInfo}>{address.line2}</Typography>}
            {address.line3 && <Typography {...styles.addressInfo}>{address.line3}</Typography>}
            <Typography {...styles.addressInfo}>
                {address.city} {address.region} {address.postalCode}
            </Typography>
            <Typography {...styles.addressInfo}>{address.country}</Typography>
        </Typography>
    );

    const btAddressRadioChange = (event: any) => {
        setAddressBtSelectedIdx(event.target.value);
    };
    const stAddressRadioChange = (event: any) => {
        setAddressStSelectedIdx(event.target.value);
    };

    const handleSubmit = () => {
        const btAddress = addresses.btValidateAddressResponse?.validatedAddresses[+addressBtSelectedIdx];
        if (useBillingAddress) {
            handleModalSubmit(btAddress, btAddress);
        } else {
            handleModalSubmit(
                btAddress,
                addresses.stValidateAddressResponse?.validatedAddresses[+addressStSelectedIdx],
            );
        }
    };

    const handleUseEntered = () => {
        handleModalSubmit();
    };

    const mapAddressRadioButton = (address: AvalaraAddressModel, idx: number) => (
        // eslint-disable-next-line react/no-array-index-key
        <Radio key={idx} {...styles.radioItem} value={`${idx}`}>
            {buildAddress(address)}
        </Radio>
    );

    // select the first suggested address when the modal is opened
    React.useEffect(() => {
        if (showModal) {
            if (useBillingAddress) {
                setAddressBtSelectedIdx("0");
            } else {
                setAddressBtSelectedIdx("0");
                setAddressStSelectedIdx("0");
            }
        }
    }, [showModal]);

    return (
        <>
            <Modal
                headline={translate("Address Validation")}
                isOpen={showModal}
                handleClose={handleModalClose}
                data-test-selector={key}
                {...styles.modalWrapper}
            >
                <StyledWrapper data-test-selector="avalaraValidationModalSection" {...styles.formWrapper}>
                    <GridContainer {...styles.mainContainer}>
                        <GridItem {...styles.mainGridItem}>
                            <Typography {...styles.description}>
                                {siteMessage("AvalaraValidationModal_Description")}
                            </Typography>
                        </GridItem>

                        <GridItem {...styles.mainGridItem}>
                            <ToastStyle
                                css={css`
                                    &&&& {
                                        margin-top: 0;
                                        width: 100%;
                                    }
                                `}
                                transitionState="entered"
                                transitionLength={0}
                                messageType="warning"
                                data-test-selector="AvalaraValidationModal_Disclaimer"
                            >
                                <ToastBody data-id="toast-body">
                                    <Typography>{siteMessage("AvalaraValidationModal_Disclaimer")}</Typography>
                                </ToastBody>
                            </ToastStyle>
                        </GridItem>

                        {useBillingAddress && (
                            <>
                                <GridItem {...styles.mainGridItem}>
                                    <Typography {...styles.label}>{translate("You Entered")}</Typography>
                                </GridItem>
                                <GridItem {...styles.mainGridItem}>{buildAddress(enteredBtAddress)}</GridItem>
                            </>
                        )}
                        {!useBillingAddress && (
                            <>
                                <GridItem {...styles.columGridItem}>
                                    <Typography {...styles.label}>{translate("Billing Address")}</Typography>
                                </GridItem>
                                <GridItem {...styles.columGridItem}>
                                    <Typography {...styles.label}>{translate("Shipping Address")}</Typography>
                                </GridItem>

                                <GridItem {...styles.columGridItem}>{buildAddress(enteredBtAddress)}</GridItem>
                                <GridItem {...styles.columGridItem}>{buildAddress(enteredStAddress)}</GridItem>
                            </>
                        )}

                        <GridItem {...styles.mainGridItem}>
                            <HorizontalDividerWidget
                                fields={{ color: colorPalette.common.global.industrialTaupe }}
                                sizeMobile={1}
                                sizeDesktop={1}
                            ></HorizontalDividerWidget>
                        </GridItem>

                        {useBillingAddress && (
                            <GridItem {...styles.mainGridItem}>
                                <RadioGroup
                                    {...styles.radioGroup}
                                    label={translate("Suggested Address")}
                                    value={addressBtSelectedIdx}
                                    onChangeHandler={btAddressRadioChange}
                                >
                                    {addresses.btValidateAddressResponse?.validatedAddresses.map((address, idx) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <Radio key={idx} {...styles.radioItem} value={`${idx}`}>
                                            {buildAddress(address)}
                                        </Radio>
                                    ))}
                                </RadioGroup>
                            </GridItem>
                        )}
                        {!useBillingAddress && (
                            <>
                                <GridItem {...styles.columGridItem}>
                                    <RadioGroup
                                        {...styles.radioGroup}
                                        label={translate("Suggested Billing Address")}
                                        value={addressBtSelectedIdx}
                                        onChangeHandler={btAddressRadioChange}
                                    >
                                        {addresses.btValidateAddressResponse?.validatedAddresses.map(
                                            mapAddressRadioButton,
                                        )}
                                    </RadioGroup>
                                </GridItem>

                                <GridItem {...styles.columGridItem}>
                                    <RadioGroup
                                        {...styles.radioGroup}
                                        label={translate("Suggested Shipping Address")}
                                        value={addressStSelectedIdx}
                                        onChangeHandler={stAddressRadioChange}
                                    >
                                        {addresses.stValidateAddressResponse?.validatedAddresses.map(
                                            mapAddressRadioButton,
                                        )}
                                    </RadioGroup>
                                </GridItem>
                            </>
                        )}

                        <GridItem {...styles.buttonsGridItem}>
                            <Button {...styles.enteredButton} type="button" onClick={handleUseEntered}>
                                {translate("Continue with Entered Address")}
                            </Button>

                            <Button {...styles.submitButton} type="button" onClick={handleSubmit}>
                                {translate("Use Suggested Address")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps)(AvalaraValidationComponentModal);
